import { useEffect, useRef, useState } from 'react';
import { getOrders } from '../services/apiClient';
import { newRefundRequestIds } from './orderSelectors';
import { playRefundAlert, unlockAudioOnFirstGesture } from '../utils/alertSound';
import logger from '../utils/logger';

// De quanto em quanto tempo se pergunta por pedidos novos. Mais curto do que o
// refresh dos pedidos (60s): o cliente tem só 1 hora depois da entrega para
// reportar um problema, e a conversa corre melhor quando lhe ligamos depressa.
const INTERVALO_MS = 30000;

const EVENTO_MUDOU = 'toms:refund-requests-changed';

/**
 * Avisa que um pedido de reembolso foi tratado aqui no painel (reembolsado ou
 * dispensado), para a bolinha do TakeAway se atualizar já. Sem isto ficava a
 * piscar até à leitura seguinte — até 30 segundos depois de o cartão ter ido
 * para o histórico, e parecia que o pedido continuava por tratar.
 */
export const notifyRefundRequestsChanged = () => {
  window.dispatchEvent(new Event(EVENTO_MUDOU));
};

/**
 * Vigia os pedidos de reembolso por tratar, em qualquer página do painel.
 *
 * Vive no topo da app e não na página dos pedidos: quem está nas reservas
 * também tem de ouvir o aviso. Toca quando entra um pedido que não estava na
 * leitura anterior, e devolve quantos há por tratar, para a barra de
 * navegação os mostrar.
 */
export const useRefundAlerts = (token, apiUrl) => {
  const [count, setCount] = useState(0);
  const conhecidos = useRef(null);

  useEffect(() => {
    unlockAudioOnFirstGesture();
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let ativo = true;

    const verificar = async () => {
      try {
        const data = await getOrders(apiUrl, token, { refund_request: 'requested', limit: 100 });
        if (!ativo) return;
        const lista = data.orders || [];
        const novos = newRefundRequestIds(conhecidos.current, lista);
        if (novos.length > 0) {
          logger.info('New refund requests', { ids: novos });
          playRefundAlert();
        }
        conhecidos.current = new Set(lista.map((o) => o.order_id));
        setCount(lista.length);
      } catch (err) {
        // Uma leitura falhada não mexe no que já se sabia: se viesse vazia, a
        // seguinte tocava por todos os pedidos que já lá estavam.
        logger.warn('Failed to check refund requests', { error: err.message });
      }
    };

    verificar();
    const intervalo = setInterval(verificar, INTERVALO_MS);
    window.addEventListener(EVENTO_MUDOU, verificar);
    return () => {
      ativo = false;
      clearInterval(intervalo);
      window.removeEventListener(EVENTO_MUDOU, verificar);
    };
  }, [token, apiUrl]);

  return count;
};
